// Canonical helper for the Wizard's Content Objectives sliders
// (awareness / education / monetizing — API/DB field names, never
// renamed). Keeps the three values linked so they always sum to
// exactly 100, redistributing the remainder between the two
// untouched sliders in proportion to their previous ratio.

export const OBJECTIVE_KEYS = ['awareness', 'education', 'monetizing'];

function toNonNegativeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Given the current {awareness, education, monetizing} values and the
// key the user just dragged to rawNewValue, returns a NEW object (the
// input is never mutated) with:
// - changedKey set to the clamped requested value;
// - the other two keys redistributed to fill the remaining percentage,
//   preserving their previous ratio to each other;
// - an equal split when both other values were zero;
// - the total always exactly 100 (the second of the two untouched
//   sliders always absorbs the rounding remainder via subtraction,
//   rather than being independently rounded, so 99/101 totals can
//   never happen).
export function redistributeObjectives(current, changedKey, rawNewValue) {
  const newValue = Math.max(0, Math.min(100, Math.round(Number(rawNewValue) || 0)));
  const [firstKey, secondKey] = OBJECTIVE_KEYS.filter((key) => key !== changedKey);

  const firstCurrent = toNonNegativeNumber(current?.[firstKey]);
  const secondCurrent = toNonNegativeNumber(current?.[secondKey]);
  const remaining = 100 - newValue;
  const othersTotal = firstCurrent + secondCurrent;

  let firstNext = othersTotal > 0
    ? Math.round((firstCurrent / othersTotal) * remaining)
    : Math.round(remaining / 2);
  firstNext = Math.max(0, Math.min(remaining, firstNext));
  const secondNext = remaining - firstNext;

  return {
    ...current,
    [changedKey]: newValue,
    [firstKey]: firstNext,
    [secondKey]: secondNext,
  };
}

// Normalizes a possibly-malformed {awareness, education, monetizing}
// triple (missing fields, non-numeric, negative, or not summing to
// 100 — e.g. loaded from an older/edited project) into one that always
// sums to exactly 100. Returns the values unchanged (not re-rounded)
// when they already sum to 100. Falls back to an equal split when the
// total is zero or invalid.
//
// Remainder-safe by construction: only the FIRST value is rounded from
// its raw proportional share (and clamped to 0-100). The SECOND is
// rounded from its own proportional share too, but then clamped to
// whatever is still available after the first (0 to remaining) — this
// is what prevents two independently-rounded values from both rounding
// up and overshooting (e.g. two values that are each exactly at a .5
// boundary would otherwise both round up and sum past 100). The THIRD
// is never rounded at all — it is always the exact remainder left
// after the first two, so the total is 100 by construction, not by
// coincidence.
export function normalizeObjectives(raw) {
  const values = OBJECTIVE_KEYS.map((key) => toNonNegativeNumber(raw?.[key]));
  const total = values.reduce((sum, value) => sum + value, 0);

  if (total === 100) {
    return OBJECTIVE_KEYS.reduce((acc, key, index) => ({ ...acc, [key]: values[index] }), {});
  }
  if (total <= 0) {
    return { awareness: 34, education: 33, monetizing: 33 };
  }

  const [firstKey, secondKey, thirdKey] = OBJECTIVE_KEYS;

  let first = Math.round((values[0] / total) * 100);
  first = Math.max(0, Math.min(100, first));
  const remainingAfterFirst = 100 - first;

  let second = Math.round((values[1] / total) * 100);
  second = Math.max(0, Math.min(remainingAfterFirst, second));

  const third = remainingAfterFirst - second;

  return { [firstKey]: first, [secondKey]: second, [thirdKey]: third };
}
